import React, { useMemo, useState } from 'react';
import Select, { SelectOption } from '@amzn/meridian/select';
import { useController, type Control, type FieldValues } from 'react-hook-form';
import { useTranslation } from 'src/arb-wrapper';
import { leelaApi } from 'src/store/leela-api';

interface FormPageIdSelectProps {
  control: Control<FieldValues> | undefined;
  name: string;
  index: number;
  pageType: string;
  required?: boolean;
  disabled?: boolean;
  testIdPrefix?: string;
}

export const FormPageIdSelect: React.FC<FormPageIdSelectProps> = ({
  control,
  name,
  index,
  pageType,
  required = false,
  disabled = false,
  testIdPrefix = 'page-id',
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch page IDs from Sonata API
  const { data: pageIds = [], isFetching } = leelaApi.useSonataApiControllerSearchPageIdsQuery(
    { pageType },
    { skip: !pageType }
  );

  // Filter page IDs based on search query (case-insensitive)
  const filteredPageIds = useMemo(() => {
    if (!searchQuery) return pageIds;
    return pageIds.filter(id => id.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [pageIds, searchQuery]);

  const { field, fieldState } = useController({
    control,
    name,
    rules: {
      validate: (value) => {
        if (required && !value) {
          return t('form_validation_required_field');
        }
        return true;
      },
    },
  });

  return (
    <Select
      data-testid={`${testIdPrefix}-${index}`}
      disabled={disabled || !pageType || isFetching}
      error={!!fieldState.error}
      errorMessage={fieldState.error?.message}
      label={required ? `${t('form_page_id')}*` : t('form_page_id')}
      placeholder={isFetching ? t('loader_loading') : t('form_page_id_placeholder')}
      popoverMaxHeight={300}
      searchQuery={searchQuery}
      value={field.value || ''}
      onChange={field.onChange}
      onSearch={setSearchQuery}
    >
      {filteredPageIds.length === 0 && searchQuery
        ? (
        <SelectOption disabled label= {t('table_no_results')}value="" />
          )
        : (
            filteredPageIds.map(pageId => (
          <SelectOption key={pageId} label={pageId} value={pageId} />
            ))
          )}
    </Select>
  );
};
